// src/utils/navPermisos.ts — Filtrado del menú lateral según permisos RBAC.
// Función pura (testeable) que decide qué ítems/grupos se muestran según el
// permiso `ver` del rol. Los grupos sin ítems visibles se omiten.
import type { PermisosMap } from '@/services/powApi'

export interface NavPermisoItem {
  to?: string
  label: string
  modulo?: string
  group?: string
  /** Módulo reservado al superadmin: no se muestra por permisos granulares. */
  soloSuperadmin?: boolean
  children?: { to: string; label: string }[]
}

/**
 * Filtra el menú por permiso de `ver`.
 * - Ítems sin `modulo` se consideran públicos (siempre visibles con sesión).
 * - Un superadmin ve todo.
 * - Los grupos quedan solo si conservan al menos un ítem visible.
 */
export function filtrarNav<T extends NavPermisoItem>(
  nav: T[],
  permisos: PermisosMap | null | undefined,
  esSuperadmin?: boolean,
): T[] {
  const puedeVer = (item: NavPermisoItem) => {
    if (item.soloSuperadmin && !esSuperadmin) return false
    if (!item.modulo) return true
    return Boolean(esSuperadmin) || Boolean(permisos?.[item.modulo]?.['ver'])
  }

  const bloques: { grupo: T | null; items: T[] }[] = [{ grupo: null, items: [] }]
  let actual = bloques[0]

  for (const item of nav) {
    if (item.group) {
      actual = { grupo: item, items: [] }
      bloques.push(actual)
      continue
    }
    if (puedeVer(item)) actual.items.push(item)
  }

  const salida: T[] = []
  for (const bloque of bloques) {
    if (bloque.items.length === 0) continue
    if (bloque.grupo) salida.push(bloque.grupo)
    salida.push(...bloque.items)
  }
  return salida
}
